import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { daysSince } from '../../utils/Utils.js';
import AnimalNotes from '../../shared/AnimalNotes.js';
import ReproductionStatus, {
  predictedDate, stepStatus, stepStatusLabelKey, stepDateRange, stepActionLabels, saveStepCompleted, stepsInDayOrder,
} from './ReproductionStatus.js';
import ReproductionResultCell from './ReproductionResultCell.js';
import '../../herd/births/HerdCardList.css';
import './ReproductionCardList.css';

const formatLotId = (lotId, prefix) =>
  lotId != null ? `${prefix}-${String(lotId).padStart(4, '0')}` : '—';

// StepRow is one protocol step in the expanded card: the same status, date,
// and actions the table's circles show on hover (phones have no hover), plus
// a full-size button to mark it done/undo. Saves exactly like a circle click.
const StepRow = ({ step, index, completed, currentDay, startDate, actions, animalId, onToggled }) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const status = stepStatus(step, completed, currentDay);
  const labels = step.system_action
    ? [`⚙️ ${t(`system_action_${step.system_action}_tooltip`)}`]
    : stepActionLabels(step, actions);

  const toggle = () => {
    setSaving(true);
    saveStepCompleted(animalId, index, !completed, () => {
      setSaving(false);
      onToggled(index, !completed);
    });
  };

  return (
    <div className={`repro-step ${status}`} data-testid="repro-step">
      <span className="repro-step-dot" title={t(stepStatusLabelKey(status))} />
      <div className="repro-step-body">
        <div className="repro-step-head">
          <span className="repro-step-day">D{step.start_day}{step.end_day !== step.start_day ? `-${step.end_day}` : ''}</span>
          <span className="repro-step-date">{stepDateRange(startDate, step)}</span>
        </div>
        <div className="repro-step-actions">
          {labels.length > 0 ? labels.join(' · ') : t('no_actions_for_step')}
        </div>
        {step.note && <div className="repro-step-note">{step.note}</div>}
      </div>
      {step.system_action ? (
        // Runs on its own — see ReproductionStatus's readOnly comment.
        <span className="repro-step-auto">{t('automatic')}</span>
      ) : (
        <button className={`repro-step-btn${completed ? ' undo' : ''}`} onClick={toggle} disabled={saving}>
          {saving ? '…' : completed ? t('undo') : t('mark_done')}
        </button>
      )}
    </div>
  );
};

const ReproductionCard = ({ row, protocol, startDate, timeline, actions, onStepToggled, onRefresh }) => {
  const { t } = useTranslation();
  const [expanded,  setExpanded]  = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const currentDay = startDate ? daysSince(startDate) : null;
  const predicted  = predictedDate(protocol, startDate);

  return (
    <div className={`herd-card repro-card ${expanded ? 'expanded' : ''}`} onClick={() => setExpanded(e => !e)}
      data-testid="repro-card">
      <div className="repro-card-main">
        <div className="repro-card-row">
          <span className="herd-card-tag">#{row.tag}</span>
          <span className="repro-card-lot">{formatLotId(row.lot_id, t('lot_prefix'))}</span>
        </div>
        <div className="repro-card-row">
          <span className="repro-card-day">{currentDay != null ? `${t('day')} ${currentDay}` : '—'}</span>
          <div onClick={e => e.stopPropagation()}>
            <ReproductionResultCell row={row} onChanged={onRefresh} />
          </div>
        </div>
        <div className="repro-card-row">
          {protocol && startDate ? (
            <ReproductionStatus
              protocol={protocol}
              animal={{ start_date: startDate, protocol: { timeline_days: timeline } }}
              readOnly
            />
          ) : <span />}
          <span className="herd-card-chevron">{expanded ? '▲' : '▼'}</span>
        </div>
      </div>

      {expanded && (
        <div className="herd-card-detail" onClick={e => e.stopPropagation()}>
          {startDate && (
            <div className="herd-card-detail-row">
              <span>{t('start_date')}</span><span>{startDate}</span>
            </div>
          )}
          {predicted && (
            <div className="herd-card-detail-row">
              <span>{t('predicted_iatf')}</span><span>{predicted}</span>
            </div>
          )}

          {protocol && startDate && (
            <div className="repro-steps">
              <div className="repro-steps-title">{t('protocol_progress')}</div>
              {stepsInDayOrder(protocol).map(({ day, index }) => (
                <StepRow
                  key={index}
                  step={day}
                  index={index}
                  completed={timeline[index]?.completed ?? false}
                  currentDay={currentDay}
                  startDate={startDate}
                  actions={actions}
                  animalId={row._id}
                  onToggled={(i, completed) => onStepToggled(row._id, i, completed)}
                />
              ))}
            </div>
          )}

          <div className="expanded-section-toggle" onClick={() => setShowNotes(v => !v)}>
            <span>{t('notes_view')}</span>
            <span className={`expanded-section-chevron${showNotes ? ' open' : ''}`}>›</span>
          </div>
          {showNotes && <AnimalNotes data={row} onNoteAdded={onRefresh} />}
        </div>
      )}
    </div>
  );
};

// ReproductionCardList is Reproduction → Animals at phone width (rendered by
// ListLotAnimals via mobileList). The card face shows progress read-only;
// steps are marked done from the expanded list, where each step shows what
// it is — the table's circles rely on hover for that and are too small to tap.
const ReproductionCardList = ({
  data, getProtocol, getProtocolIdForLot, getStartDateForLot, timelineFor, onStepToggled, actions, onRefresh,
}) => (
  <div className="herd-card-list">
    {(data || []).map(row => {
      const protocol = getProtocol(getProtocolIdForLot(row.lot_id));
      return (
        <ReproductionCard
          key={row._id}
          row={row}
          protocol={protocol}
          startDate={getStartDateForLot(row.lot_id)}
          timeline={protocol ? timelineFor(row, protocol) : []}
          actions={actions}
          onStepToggled={onStepToggled}
          onRefresh={onRefresh}
        />
      );
    })}
    {(!data || data.length === 0) && <div className="herd-card-empty">—</div>}
  </div>
);

export default ReproductionCardList;
